import React from 'react';
import { cn } from '../../../utils/cn';

export interface LinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  /** Link variant */
  variant?: 'default' | 'subtle' | 'accent' | 'destructive';
  /** Whether to show an external link icon */
  external?: boolean;
  /** Whether the link is disabled */
  disabled?: boolean;
}

const linkVariants = {
  default: [
    'text-primary-600 hover:text-primary-700',
    'dark:text-primary-400 dark:hover:text-primary-300',
  ],
  subtle: [
    'text-neutral-600 hover:text-neutral-900',
    'dark:text-neutral-400 dark:hover:text-neutral-100',
  ],
  accent: [
    'text-accent-600 hover:text-accent-700',
    'dark:text-accent-400 dark:hover:text-accent-300',
  ],
  destructive: [
    'text-error-600 hover:text-error-700',
    'dark:text-error-400 dark:hover:text-error-300',
  ],
};

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  ({ className, variant = 'default', external = false, disabled = false, children, ...props }, ref) => {
    const Component = disabled ? 'span' : 'a';
    
    return (
      <Component
        className={cn(
          // Base styles
          'inline-flex items-center gap-1',
          'font-medium underline-offset-4',
          'transition-colors duration-fast ease-luxury',
          'focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:ring-offset-2',
          'hover:underline',
          // Variant styles
          !disabled && linkVariants[variant],
          // Disabled styles
          disabled && [
            'text-neutral-400 cursor-not-allowed',
            'dark:text-neutral-600',
          ],
          className
        )}
        ref={disabled ? undefined : ref}
        {...(disabled ? {} : props)}
      >
        {children}
        {external && !disabled && (
          <svg
            className="w-3 h-3 ml-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        )}
      </Component>
    );
  }
);

Link.displayName = 'Link';